//core
import React, {useCallback, useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Menu, MenuItem, Divider } from "@mui/material";

//icons
import {MoreVert, Close, Edit} from "@mui/icons-material";

//styles
import { useStyles } from "./styles";

//routes
import routes from "../../../../../../../../../navigation/routes";

//hooks
import { usePermissions } from "../../../../../../../../../store/common/configuration/usePermissions";
import { useCreatives } from "../../../../../../../../../store/admin/сreativesV2/useCreatives";

//components
import MuiModal from "../../../../../../../../UI/MuiModal";
import DeleteFlatFee from "../../../../../DeleteFlatFee";
import { itemListData } from "../../../../../../../../common/TableData";
import {useFlatFee} from "../../../../../../../../../store/admin/flatFee/useFlatFee";
import CreateUpdateFlatFee from "../../../../../CreateUpdateFlatFee";
import { PaymentIcon } from "../../../../../../../../UI/Icons";
import Payouts from "../../../../../Payouts";

export interface ICardMenuProps {
  data: itemListData;
}

const CardMenu: React.FC<ICardMenuProps> = ({
  data
}): JSX.Element => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { hasPermissions } = usePermissions();
	const { updateFlatFee, deleteFlatFee } = useFlatFee();

	const hasDelete = hasPermissions(["api3.admin.flatfeecontroller.annulate"]);
	const hasUpdate = hasPermissions(["api3.admin.flatfeecontroller.edit"]);
	const isPermissionShowPayment = hasPermissions(["api3.admin.flatfeecontroller.payment_create"]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const open = Boolean(anchorEl);
	const [showModalUpdate, setShowModalUpdate] = useState(false);
	const [showModalPayment, setShowModalPayment] = useState(false);

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, [setAnchorEl]);
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  },[setAnchorEl]);

  const handleToggleDeleteModal = useCallback((state: boolean) => {
    setShowDeleteModal(state);
  }, [data.id, setShowDeleteModal]);

	const handleClickDelete = useCallback(() => {
		deleteFlatFee(data.id);
		setShowDeleteModal(false);
		handleClose();
	},[data.id]);

	const handleOpenUpdateFlatFee = useCallback((state: boolean) => {
		setShowModalUpdate(state);
	}, [data.id, setShowModalUpdate]);

	const toggleModalPayment = useCallback((state: boolean) => {
		setShowModalPayment(state);
	}, [data.id, setShowModalPayment, showModalPayment]);

  return (
    <>
      <Button
        className={`aff-card-menu ${styles.button} ${ open ? styles.active : ''}`}
        color="secondary"
        onClick={handleClick}
        id="aff-card-menu"
        aria-controls={open ? 'basic-card-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <MoreVert className={styles.icon} />
      </Button>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'tracker-card-menu',
          sx: {
            paddingTop: 0,
            paddingBottom: 0,
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
	      {hasUpdate && (
		      <MenuItem
			      onClick={() => handleOpenUpdateFlatFee(true)}
			      disabled={data.status?.toLowerCase() === "annulated" || data.status?.toLowerCase() === "closed"}
		      >
			      <Edit className={styles.icon} sx={{ marginRight: '16px' }} />
			      {t("common.buttons.edit")}
		      </MenuItem>
	      )}
	      {hasUpdate && <Divider className={styles.divider}/>}
        {hasDelete && (
          <MenuItem
            onClick={() => handleToggleDeleteModal(true)}
            disabled={data.status?.toLowerCase() === "annulated" || data.status?.toLowerCase() === "closed"}
          >
            <Close className={styles.icon} sx={{ marginRight: '16px' }} />
	          {t("common.buttons.annul")}
          </MenuItem>
        )}
	      {hasDelete && <Divider className={styles.divider}/>}

	      {isPermissionShowPayment && (
          <MenuItem
            onClick={() => toggleModalPayment(true)}
          >
            <PaymentIcon className={styles.icon} sx={{ marginRight: '16px' }} />
	          {t("common.buttons.payment")}
          </MenuItem>
        )}

      </Menu>

	    {hasDelete && (<MuiModal
		    width={320}
		    open={showDeleteModal}
		    closeOnOutside={false}
		    setToggleModal={handleToggleDeleteModal}
	    >
		    <DeleteFlatFee
			    onAccept={handleClickDelete}
			    setToggleModal={handleToggleDeleteModal}
			    id={data.id}
		    />
	    </MuiModal>)}
	    {hasUpdate && (<MuiModal
		    width={496}
		    open={showModalUpdate}
		    closeOnOutside={false}
		    setToggleModal={setShowModalUpdate}
	    >
		    <CreateUpdateFlatFee
			    data={data}
			    closeModal={handleOpenUpdateFlatFee}
			    handleSubmitForm={updateFlatFee}
			    title={"admin.flatFee.createUpdateForm.update_title"}
		    />
	    </MuiModal>)}

	    {isPermissionShowPayment && (
		    <MuiModal
			    width={496}
			    open={showModalPayment}
			    closeOnOutside={false}
			    setToggleModal={toggleModalPayment}
		    >
			    <Payouts
				    paymentData={data}
				    affiliateId={data.affiliate_id}
				    flatFeeId={data.id}
			    />
		    </MuiModal>
	    )}
    </>
  );
};

export default CardMenu;
